import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { personalInfo, skills } from '../mock';
import { ArrowLeft } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-neutral-950/80 backdrop-blur-md border-b border-neutral-800 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-white hover:text-gray-300 transition-colors">
            {personalInfo.name.split(' ').map((word, i) => i === 0 ? word[0] : '').join('')}VT
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/projects" className="text-neutral-300 hover:text-white transition-colors">Projects</Link>
            <Link to="/about" className="text-white font-semibold">About</Link>
            <Link to="/contact">
              <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white">
                Contact Me
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">About Me</h1>
          </div>

          {/* Bio Section */}
          <div className="mb-20">
            <Card className="bg-neutral-900 border-neutral-800">
              <CardContent className="pt-8">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="flex-shrink-0">
                    <img 
                      src={personalInfo.aboutImage} 
                      alt={personalInfo.name}
                      className="w-64 h-64 object-cover rounded-xl border-4 border-neutral-800"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-300 mb-4">Hello there!</h3>
                    <p className="text-neutral-300 text-lg leading-relaxed mb-6">
                      {personalInfo.bio}
                    </p>
                    <p className="text-xl font-semibold text-white">
                      Let's collaborate and bring unique game ideas to life!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Development Skills */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-white mb-8">Development Skills</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Programming Languages */}
              <Card className="bg-neutral-900 border-neutral-800">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-300">Programming Languages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {skills.programmingLanguages.map((skill, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg hover:bg-neutral-700 transition-colors">
                        <span className="text-white font-medium">{skill.name}</span>
                        <Badge className="bg-gray-700 text-white hover:bg-gray-600">
                          {skill.level}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Software/Tools */}
              <Card className="bg-neutral-900 border-neutral-800">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-300">Software/Tools</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {skills.softwareTools.map((skill, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg hover:bg-neutral-700 transition-colors">
                        <span className="text-white font-medium">{skill.name}</span>
                        <Badge className="bg-gray-700 text-white hover:bg-gray-600">
                          {skill.level}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Other Skills */}
          <div>
            <h2 className="text-4xl font-bold text-white mb-8">Other Skills</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Soft Skills */}
              <Card className="bg-neutral-900 border-neutral-800">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-300">Soft Skills</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {skills.softSkills.map((skill, index) => (
                      <Badge key={index} className="bg-neutral-800 text-neutral-200 hover:bg-gray-700 hover:text-white px-4 py-2 text-sm">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Languages */}
              <Card className="bg-neutral-900 border-neutral-800">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-300">Languages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {skills.languages.map((lang, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg hover:bg-neutral-700 transition-colors">
                        <span className="text-white font-medium">{lang.name}</span>
                        <Badge className="bg-gray-700 text-white hover:bg-gray-600">
                          {lang.level}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-neutral-800">
        <div className="max-w-7xl mx-auto text-center text-neutral-400">
          <p>&copy; 2025 {personalInfo.name}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default About;